import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Wifi, Printer, KeyRound, Coffee } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DURATIONS = ["Half day (4 hrs)", "Full day (8 hrs)", "Weekly (5 days)", "Monthly"];
const PRICING = [
  { label: "Day Pass", price: "$45", period: "/ day" },
  { label: "Weekly", price: "$180", period: "/ 5 days" },
  { label: "Monthly", price: "$550", period: "/ month" },
];

const INCLUDED = [
  "1 Monitor",
  "Internet cable",
  "Keyboard, mouse & camera",
  "Headphones",
  "Docking station + HDMI",
  "Separate guest Wi-Fi",
  "Printer + stationery (pens, books)",
  "Secure code + key on arrival",
  "Access to shared kitchen, pantry & coffee machine",
];

const HERO_IMG = "/img/hotdesk_row.png";
const SIDE_IMG = "/img/hotdesk_corner.png";
const EXTRA_IMG_A = "/img/leasing_two.png";   // SABA Fashion Fabrics — also used here per owner
const EXTRA_IMG_B = "/img/leasing_three.png"; // brick pillar room — also used here

const initialForm = {
  name: "", email: "", phone: "", preferred_date: "",
  duration: DURATIONS[1], seats: 1, message: "",
};

const inputCls =
  "w-full bg-transparent border-b border-[#E6DFD3] py-3 text-[#2D231E] font-sans placeholder-[#A88B6C] focus:outline-none focus:border-[#4A3C31] transition-colors rounded-none";
const labelCls =
  "block text-[11px] font-sans tracking-[0.25em] uppercase text-[#5D4037] mb-2";

export default function HotDeskingPage() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.preferred_date || !form.duration) {
      setError("Please fill in name, email, preferred date and duration.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/desk-hire-enquiries`, { ...form, seats: Number(form.seats) || 1 });
      setSubmitted(true);
      setForm(initialForm);
    } catch {
      setError("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="hot-desking-page" className="animate-in fade-in duration-500">
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pt-16 sm:pt-24 pb-12 sm:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-6">Service 02 · Toowoomba</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight text-[#2D231E] leading-[1.02] mb-6">
              Hot Desk <span className="italic text-[#8B7355]">Hire</span>
            </h1>
            <p className="font-serif text-xl sm:text-2xl italic text-[#4A3C31] max-w-2xl mb-5">
              A warm, quiet desk to call your own.
            </p>
            <p className="font-sans text-base text-[#5D4037] max-w-xl">
              Sat in the heart of the Toowoomba CBD, walking distance to plenty
              of foodary and right next to Grand Central. Bring your laptop and
              charger — we've got everything else.
            </p>
          </div>
          <div className="lg:col-span-6">
            <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#E6DFD3]">
              <img src={HERO_IMG} alt="Hot desk row at Saba Workspace" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-16 sm:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#E6DFD3]">
              <img src={SIDE_IMG} alt="Hot desk corner setup" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-3">What's included</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#2D231E] mb-5">Everything but the laptop.</h2>
            <p className="font-sans text-sm sm:text-base text-[#5D4037] mb-6 max-w-xl">
              Each hot desk comes set up so you can sit down and start working.
              The space is secure, coded entry, and your own key is issued on arrival.
            </p>
            <ul data-testid="hot-desk-inclusions" className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 font-sans text-sm text-[#5D4037]">
              {INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span className="mt-2 inline-block w-1 h-1 rounded-full bg-[#8B7355] shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-7">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><Wifi size={12} strokeWidth={1.5}/>Separate Wi-Fi</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><KeyRound size={12} strokeWidth={1.5}/>Coded Entry + Key</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><Printer size={12} strokeWidth={1.5}/>Printer + Stationery</span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#E6DFD3] rounded-sm font-sans text-[10px] tracking-[0.25em] uppercase text-[#5D4037]"><Coffee size={12} strokeWidth={1.5}/>Coffee + Pantry</span>
            </div>
          </div>
        </div>
      </section>

      {/* Additional space photos */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-16 sm:pb-20">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-5">More of the Saba building</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 lg:gap-7">
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#E6DFD3]">
            <img src={EXTRA_IMG_A} alt="Saba building character — exposed brick + vintage signage" className="w-full h-full object-cover" />
          </div>
          <div className="relative aspect-[4/3] overflow-hidden rounded-sm border border-[#E6DFD3]">
            <img src={EXTRA_IMG_B} alt="Saba building character — brick pillars and natural light" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Pricing + Enquiry */}
      <section data-testid="desk-hire-section" id="enquire" className="max-w-7xl mx-auto px-6 sm:px-12 pb-24 sm:pb-32">
        <div className="bg-[#FAF9F6] border border-[#E6DFD3] rounded-sm p-8 sm:p-12">
          <div className="mb-8">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#8B7355] mb-3">Pricing & Enquiry</p>
            <h3 className="font-serif text-3xl sm:text-4xl text-[#2D231E] mb-3">Request a desk.</h3>
            <p className="font-sans text-sm sm:text-base text-[#5D4037] max-w-lg">
              Send through your details and someone from the Saba Workspace team will
              be in contact with you shortly to arrange your desk and dates.
            </p>
          </div>

          <div data-testid="desk-hire-pricing" className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10">
            {PRICING.map((p) => (
              <div key={p.label} className="flex items-baseline justify-between gap-4 border border-[#E6DFD3] px-5 py-4 bg-[#F9F6F0] rounded-sm">
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#8B7355]">{p.label}</p>
                <p className="font-serif text-2xl sm:text-3xl text-[#2D231E] leading-none">
                  {p.price}<span className="font-sans text-xs tracking-wide text-[#8B7355] ml-1">{p.period}</span>
                </p>
              </div>
            ))}
          </div>
          <p className="font-sans text-[11px] text-[#8B7355] italic -mt-8 mb-10">* Indicative pricing — final quote sent after we confirm your dates.</p>

          {submitted ? (
            <div data-testid="desk-hire-success" className="border border-[#E6DFD3] p-8 sm:p-10 rounded-sm bg-[#F9F6F0] flex flex-col items-start gap-4">
              <CheckCircle2 size={28} strokeWidth={1.5} className="text-[#8B7355]" />
              <h4 className="font-serif text-2xl sm:text-3xl text-[#2D231E]">Enquiry received.</h4>
              <p className="font-sans text-sm sm:text-base text-[#5D4037] max-w-md">
                Thanks for reaching out — someone from the Saba Workspace team will be in
                contact with you shortly to arrange your desk and dates.
              </p>
              <button type="button" onClick={() => setSubmitted(false)} className="mt-2 inline-flex items-center justify-center px-6 py-3 bg-transparent border border-[#4A3C31] text-[#4A3C31] font-sans text-xs tracking-[0.25em] uppercase hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors rounded-sm">
                Send another
              </button>
            </div>
          ) : (
            <form data-testid="desk-hire-form" onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
              <div><label className={labelCls} htmlFor="name">Name</label><input id="name" name="name" value={form.name} onChange={onChange} required placeholder="Your name" data-testid="desk-hire-name" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="email">Email</label><input id="email" name="email" type="email" value={form.email} onChange={onChange} required placeholder="you@example.com" data-testid="desk-hire-email" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="phone">Phone (optional)</label><input id="phone" name="phone" type="tel" value={form.phone} onChange={onChange} placeholder="04xx xxx xxx" data-testid="desk-hire-phone" className={inputCls} /></div>
              <div><label className={labelCls} htmlFor="preferred_date">Preferred start date</label><input id="preferred_date" name="preferred_date" type="date" value={form.preferred_date} onChange={onChange} required data-testid="desk-hire-date" className={inputCls} /></div>
              <div>
                <label className={labelCls} htmlFor="duration">Duration</label>
                <select id="duration" name="duration" value={form.duration} onChange={onChange} required data-testid="desk-hire-duration" className={`${inputCls} appearance-none bg-[#F9F6F0]`}>
                  {DURATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div><label className={labelCls} htmlFor="seats">Number of desks</label><input id="seats" name="seats" type="number" min={1} max={20} value={form.seats} onChange={onChange} required data-testid="desk-hire-seats" className={inputCls} /></div>
              <div className="sm:col-span-2"><label className={labelCls} htmlFor="message">Message (optional)</label><textarea id="message" name="message" rows={4} value={form.message} onChange={onChange} placeholder="Anything we should know? Equipment, accessibility, special requests…" data-testid="desk-hire-message" className={`${inputCls} resize-none`} /></div>
              {error && <p data-testid="desk-hire-error" className="sm:col-span-2 font-sans text-sm text-[#9D5B5B]">{error}</p>}
              <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                <p className="font-sans text-xs text-[#8B7355]">We'll never share your details.</p>
                <button type="submit" disabled={submitting} data-testid="desk-hire-submit" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#4A3C31] text-[#F9F6F0] font-sans text-xs tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm disabled:opacity-60">
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {submitting ? "Sending…" : "Send Enquiry"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
